// src/components/EventDetailModal.jsx
import React, { useEffect, useState } from "react";
import styles from "./EventDetailModal.module.css";
import api from "../api";
import { isActiveStaff } from "../utils/contractEnded";
import {
  TITLE_INPUT_TYPES, PERFORMANCE_INPUT_TYPES, REQUEST_ONLY_LEAVE_TYPES, REQUEST_ONLY_NOTICE,
  currentUser, isCalendarAdmin, selectableEventTypesFor,
} from "../constants/eventTypes";

export default function EventDetailModal({
  isOpen,
  onClose,
  event,
  onUpdate,
  onDelete,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [users, setUsers] = useState([]);

  // ✅ 업무종류 목록 + 장소 자동완성
  const [workCategories, setWorkCategories] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  // 이벤트 데이터 로드
  useEffect(() => {
    if (event) {
      setEditData({
        title: event.title,
        event_type: event.event_type,
        start_date: event.start_date,
        end_date: event.end_date,
        start_time: event.start_time ? event.start_time.slice(0, 5) : "",
        end_time: event.end_time ? event.end_time.slice(0, 5) : "",
        is_notice: event.is_notice,
        description: event.description || "",
        assignee: event.assignee_id || event.assignee || "",
        break_hours: event.break_hours ?? 0,
        // ✅ 업무일정 전용
        work_category: event.work_category ?? "",
        location: event.location ?? "",
        difficulty: event.difficulty ?? "",
        travel_hours: event.travel_hours ?? 0,
      });
    }
  }, [event]);

  // 사용자 목록 + 업무종류 불러오기
  useEffect(() => {
    if (isOpen) {
      api
        .get("/users/staff-contacts/")
        .then((res) => setUsers(res.data))
        .catch((err) => console.error("유저 목록 불러오기 실패", err));

      api
        .get("/calendar/work-categories/")
        .then((res) => setWorkCategories(res.data || []))
        .catch((err) => console.error("업무종류 불러오기 실패", err));
    }
  }, [isOpen]);

  // 장소 자동완성
  useEffect(() => {
    if (!isOpen || !isEditing) return;
    if (editData.event_type !== "업무일정") return;

    const handler = setTimeout(() => {
      api
        .get("/calendar/locations/suggest/", {
          params: { q: editData.location || "" },
        })
        .then((res) => setLocationSuggestions(res.data || []))
        .catch(() => setLocationSuggestions([]));
    }, 200);

    return () => clearTimeout(handler);
  }, [editData.location, editData.event_type, isOpen, isEditing]);

  // 관리자가 아니면 연차·반차로 바꿀 수 없다(2026-10-19) — 서버도 막는다
  const me = currentUser();
  const isAdmin = isCalendarAdmin(me);
  const eventTypes = selectableEventTypesFor(me);
  // 수정·삭제 버튼: 작성자 본인 또는 관리자(서버 권한과 같은 기준). 연차·반차는 관리자만.
  // ⚠️ 예전엔 window.currentUserId·currentUserRank를 봤는데 어디서도 채우지 않아서, 일정의 author_id(없는 필드)와
  // undefined === undefined로 비교돼 모든 사용자에게 버튼이 보였다(누르면 서버가 403).
  const isRequestOnly = REQUEST_ONLY_LEAVE_TYPES.includes(event?.event_type);
  const canModify = isAdmin || (event?.author === me.id && !isRequestOnly);

  const generateTimeOptions = () => {
    const opts = [];
    for (let hour = 6; hour <= 24; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const h = hour === 24 ? 0 : hour;
        const val = `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        const disp = hour === 24 ? `24:${String(minute).padStart(2, "0")}` : val;
        opts.push({ value: val, display: disp });
      }
    }
    return opts;
  };
  const timeOptions = generateTimeOptions();

  const handleInputChange = (field, value) => {
    setEditData((p) => ({ ...p, [field]: value }));
  };

  const handleSave = async () => {
    if (!event) return;
    if (saving) return;

    // ✅ 유형별 필수값 체크
    if (editData.event_type === "행사" && !editData.title?.trim()) {
      alert("제목을 입력해 주세요.");
      return;
    }
    if (PERFORMANCE_INPUT_TYPES.includes(editData.event_type) && !editData.work_category) {
      alert("업무 항목을 선택해 주세요.");
      return;
    }

    setSaving(true);
    try {

      
      const payload = {
        event_type: editData.event_type,
        start_date: editData.start_date,
        end_date: editData.end_date,
        start_time: editData.start_time ? editData.start_time + ":00" : null,
        end_time: editData.end_time ? editData.end_time + ":00" : null,
        is_notice: !!editData.is_notice,
        description: editData.description,
        assignee: editData.assignee || null,
        break_hours: editData.break_hours || 0,
      };

      // ✅ 제목 직접 입력 유형(행사·근무시간 변동)
      // 근무시간 변동은 비워 보내도 서버가 유형명으로 채운다(모델 save).
      if (TITLE_INPUT_TYPES.includes(editData.event_type)) {
        payload.title = editData.title;
      }

      // ✅ 업무일정: 항목/장소/난이도/이동시간 (제목 서버 자동생성)
      if (PERFORMANCE_INPUT_TYPES.includes(editData.event_type)) {
        payload.work_category = editData.work_category;
        payload.location = editData.location;
        payload.difficulty = editData.difficulty ? parseInt(editData.difficulty) : null;
        payload.travel_hours = editData.travel_hours;
      }
      // 휴가 4종은 title 안 보냄 → 서버 자동생성

      const res = await api.patch(`/calendar/events/${event.id}/`, payload);
      onUpdate?.(res.data);
      setIsEditing(false);

    } catch (err) {
      const data = err?.response?.data;
      let msg = "알 수 없는 오류가 발생했습니다.";
      if (data?.non_field_errors?.[0]) {
        msg = data.non_field_errors[0];   // 잔여시간 부족 메시지
      } else if (data?.assignee) {
        msg = "담당자를 선택해 주세요.";
      } else if (data?.title) {
        msg = "제목을 입력해 주세요.";
      } else if (typeof data === "string") {
        msg = data;
      }
      alert("저장 실패: " + msg);
      console.error("EventDetailModal 저장 오류", err);
    } finally {
      setSaving(false);
    }


  };

  const handleDelete = async () => {
    if (!event) return;
    if (!window.confirm("이 이벤트를 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/calendar/events/${event.id}/`);
      onDelete?.(event.id);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "삭제 실패";
      alert(msg);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose?.();
  };

  if (!isOpen || !event) return null;

  const displayAuthor = event.author_display || event.author || "작성자";
  const displayAssignee = event.assignee_display || event.assignee || "미지정";

  // ✅ 이동시간 표시용 라벨
  const travelLabel = (h) => {
    const map = { 0: "없음", 0.5: "30분", 1: "1시간", 1.5: "1시간 30분", 2: "2시간" };
    return map[h] ?? `${h}시간`;
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.menuButton} type="button">
              <span className={styles.menuIcon}>☰</span>
            </button>
          </div>
          <button className={styles.closeButton} onClick={handleClose} type="button">
            <span className={styles.closeIcon}>×</span>
          </button>
        </div>

        <div className={styles.content}>
          {!isEditing ? (
            <>
              {/* 담당자 */}
              <div className={styles.authorSection}>
                <div className={styles.authorLabel}>담당자</div>
                <div className={styles.authorInfo}>{displayAssignee}</div>
              </div>

              {/* 제목 */}
              <div className={styles.titleSection}>
                <h2 className={styles.title}>{event.title}</h2>
                <div className={styles.eventType}>{event.event_type}</div>
                {event.is_notice && (
                  <div className={styles.noticeBadge}>📌 공지사항</div>
                )}
              </div>

              {/* ✅ 업무일정 전용 정보 표시 */}
              {event.event_type === "업무일정" && (
                <div className={styles.createdSection}>
                  {event.work_category_name && (
                    <div style={{ marginBottom: "6px" }}>
                      <span className={styles.createdLabel}>업무 항목</span>
                      <div className={styles.createdInfo}>{event.work_category_name}</div>
                    </div>
                  )}
                  {event.location && (
                    <div style={{ marginBottom: "6px" }}>
                      <span className={styles.createdLabel}>장소</span>
                      <div className={styles.createdInfo}>{event.location}</div>
                    </div>
                  )}
                  {event.difficulty != null && event.difficulty !== "" && (
                    <div style={{ marginBottom: "6px" }}>
                      <span className={styles.createdLabel}>난이도</span>
                      <div className={styles.createdInfo}>{event.difficulty} / 10</div>
                    </div>
                  )}
                  <div>
                    <span className={styles.createdLabel}>이동시간</span>
                    <div className={styles.createdInfo}>{travelLabel(event.travel_hours || 0)}</div>
                  </div>
                </div>
              )}

              {/* 작성자 */}
              <div className={styles.authorSection}>
                <div className={styles.authorLabel}>작성자</div>
                <div className={styles.authorInfo}>{displayAuthor}</div>
              </div>

              {/* 일정 시간 */}
              <div className={styles.datetimeSection}>
                <div className={styles.dateRange}>
                  {event.start_date === event.end_date
                    ? new Date(event.start_date).toLocaleDateString("ko-KR", {
                        month: "long", day: "numeric", weekday: "long",
                      })
                    : `${new Date(event.start_date).toLocaleDateString("ko-KR", {
                        month: "long", day: "numeric",
                      })} - ${new Date(event.end_date).toLocaleDateString("ko-KR", {
                        month: "long", day: "numeric",
                      })}`}
                </div>
                <div className={styles.timeRange}>
                  {event.is_notice
                    ? "공지사항 이벤트"
                    : `${event.start_time} - ${event.end_time}`}
                </div>
              </div>

              {/* 설명 */}
              {event.description && (
                <div className={styles.descriptionSection}>
                  <div className={styles.descriptionHeader}>📝 설명</div>
                  <div className={styles.descriptionContent}>{event.description}</div>
                </div>
              )}

              {/* 휴게시간 */}
              {event.break_hours > 0 && (
                <div className={styles.createdSection}>
                  <div className={styles.createdLabel}>휴게시간</div>
                  <div className={styles.createdInfo}>
                    {event.break_hours === 0.5 ? "30분" : `${event.break_hours}시간`}
                  </div>
                </div>
              )}

              {/* 생성일 */}
              {event.created_at && (
                <div className={styles.createdSection}>
                  <div className={styles.createdLabel}>생성일</div>
                  <div className={styles.createdInfo}>
                    {new Date(event.created_at).toLocaleString("ko-KR")}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* ✅ 제목 직접 입력 유형(행사·근무시간 변동) */}
              {TITLE_INPUT_TYPES.includes(editData.event_type) && (
                <div className={styles.inputRow}>
                  <span className={styles.inputLabelInline}>제목</span>
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    className={styles.inlineInput}
                    placeholder="제목을 입력하세요"
                  />
                </div>
              )}

              {/* 담당자 드롭다운 */}
              <div className={styles.inputRow}>
                <span className={styles.inputLabelInline}>담당자</span>
                <select
                  value={editData.assignee}
                  onChange={(e) => handleInputChange("assignee", e.target.value)}
                  className={styles.inlineInput}
                >
                  <option value="">담당자 선택</option>
                  {users
                    // 계약종료자는 제외하되, 이 일정의 현재 담당자는 남긴다 — 과거 일정을 열면
                    // 선택칸이 비어 보이지 않게(조회 유지).
                    .filter(u => u.name && u.name !== "테스트" &&
                      (isActiveStaff(u) || String(u.id) === String(editData.assignee)))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} {u.position}
                      </option>
                    ))}
                </select>
              </div>

              {/* 일정 유형 */}
              <div className={styles.typeSection}>
                {eventTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={
                      editData.event_type === type
                        ? styles.typeButtonActive
                        : styles.typeButton
                    }
                    onClick={() => handleInputChange("event_type", type)}
                  >
                    {type}
                  </button>
                ))}
              </div>

              {/* ✅ 업무일정: 항목/장소/난이도/이동시간 */}
              {editData.event_type === "업무일정" && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.inputLabel}>업무 항목</label>
                    <select
                      value={editData.work_category}
                      onChange={(e) => handleInputChange("work_category", e.target.value)}
                    >
                      <option value="">항목 선택</option>
                      {workCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.inputLabel}>장소</label>
                    <input
                      type="text"
                      value={editData.location}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      placeholder="예: ○○어린이집"
                      list="detail-location-suggestions"
                      className={styles.inlineInput}
                    />
                    <datalist id="detail-location-suggestions">
                      {locationSuggestions.map((loc, i) => (
                        <option key={i} value={loc} />
                      ))}
                    </datalist>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.inputLabel}>업무 난이도 (1~10)</label>
                    <select
                      value={editData.difficulty}
                      onChange={(e) => handleInputChange("difficulty", e.target.value)}
                    >
                      <option value="">선택 안함</option>
                      {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.inputLabel}>이동시간</label>
                    <select
                      value={editData.travel_hours}
                      onChange={(e) => handleInputChange("travel_hours", parseFloat(e.target.value))}
                    >
                      <option value={0}>없음</option>
                      <option value={0.5}>30분</option>
                      <option value={1}>1시간</option>
                      <option value={1.5}>1시간 30분</option>
                      <option value={2}>2시간</option>
                    </select>
                  </div>
                </>
              )}

              {/* 날짜 */}
              <div className={styles.dateRow}>
                <div>
                  <label className={styles.inputLabel}>시작 날짜</label>
                  <input
                    type="date"
                    value={editData.start_date}
                    onChange={(e) => handleInputChange("start_date", e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
                <div>
                  <label className={styles.inputLabel}>종료 날짜</label>
                  <input
                    type="date"
                    value={editData.end_date}
                    onChange={(e) => handleInputChange("end_date", e.target.value)}
                    className={styles.dateInput}
                    min={editData.start_date}
                  />
                </div>
              </div>

              {/* 공지사항 체크 */}
              <div className={styles.noticeSection}>
                <label>
                  <input
                    type="checkbox"
                    checked={editData.is_notice}
                    onChange={(e) => handleInputChange("is_notice", e.target.checked)}
                  />
                  공지사항
                </label>
              </div>

              {/* 시작 시간 */}
              {!editData.is_notice && (
                <div className={styles.formGroup}>
                  <label className={styles.inputLabel}>시작 시간</label>
                  <select
                    value={editData.start_time}
                    onChange={(e) => handleInputChange("start_time", e.target.value)}
                    className={styles.timeSelect}
                  >
                    {timeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.display}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* 휴게시간 */}
              <div className={styles.formGroup}>
                <label className={styles.inputLabel}>휴게시간</label>
                <select
                  value={editData.break_hours}
                  onChange={(e) => handleInputChange("break_hours", parseFloat(e.target.value))}
                  className={styles.inlineInput}
                >
                  <option value={0}>없음</option>
                  <option value={0.5}>30분</option>
                  <option value={1}>1시간</option>
                </select>
              </div>

              {/* 종료 시간 */}
              {!editData.is_notice && (
                <div className={styles.formGroup}>
                  <label className={styles.inputLabel}>종료 시간</label>
                  <select
                    value={editData.end_time}
                    onChange={(e) => handleInputChange("end_time", e.target.value)}
                    className={styles.timeSelect}
                  >
                    {timeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.display}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* 설명 */}
              <div className={styles.formGroup}>
                <label className={styles.inputLabel}>설명</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className={styles.descriptionInput}
                  placeholder="설명을 입력하세요"
                />
              </div>
            </>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {!isEditing && canModify && (
              <button onClick={handleDelete} className={styles.deleteButton} type="button">
                삭제
              </button>
            )}
            {!isEditing && !isAdmin && isRequestOnly && (
              <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
                {event.source_document ? "휴가신청서로 승인된 일정 — 취소는 캘린더 관리자에게" : REQUEST_ONLY_NOTICE}
              </span>
            )}
          </div>
          <div className={styles.footerRight}>
            {!isEditing ? (
              <>
                <button onClick={handleClose} className={styles.cancelButton} type="button">
                  닫기
                </button>
                {canModify && (
                  <button onClick={() => setIsEditing(true)} className={styles.editButton} type="button">
                    수정
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className={styles.cancelButton}
                  type="button"
                  disabled={saving}
                >
                  취소
                </button>
                <button
                  onClick={handleSave}
                  className={styles.saveButton}
                  type="button"
                  disabled={saving}
                >
                  {saving ? "저장 중…" : "저장"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}